import { NavLink } from 'react-router-dom';
import { Button } from '@/components/ui-next';

const TEAM_METRICS = [
  {
    label: 'Active Conversations',
    value: '0',
    icon: 'chat',
    helper: 'No active threads right now',
  },
  {
    label: 'Calls Today',
    value: '0',
    icon: 'call',
    helper: 'Voice volume will appear here',
  },
  {
    label: 'Avg Response Time',
    value: '—',
    icon: 'timer',
    helper: 'Needs conversation history',
  },
];

const QUICK_START_STEPS = [
  {
    label: 'Connect Twilio numbers',
    description: 'Attach your SMS and voice numbers to start routing conversations.',
    to: '/settings',
    cta: 'Open settings',
  },
  {
    label: 'Verify team availability',
    description: 'Confirm voice-ready agents so inbound calls can be assigned instantly.',
    to: '/agents',
    cta: 'Review agents',
  },
  {
    label: 'Open live comms workspace',
    description: 'Use the dialer to monitor transcripts, summaries, and follow-ups in one place.',
    to: '/dialer',
    cta: 'Go to comms',
  },
];

export function DashboardPage() {
  return (
    <div className="space-y-5">
      <header className="shell-surface p-5 sm:p-6">
        <p className="inline-flex rounded-full border border-border bg-background/85 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Team Inbox
        </p>
        <h2 className="mt-3 text-2xl font-semibold text-foreground sm:text-[2rem]">Dashboard</h2>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Your shared team inbox and overview. Track live activity, route customers faster, and keep
          operations aligned across calls and messaging.
        </p>
      </header>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[1.4fr_1fr]">
        <div className="shell-surface relative overflow-hidden border-dashed p-8 sm:p-10">
          <div
            aria-hidden
            className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-brand-500/10 blur-2xl"
          />
          <div className="relative">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-500/10">
              <span className="material-symbols-outlined text-[32px] text-brand-500">inbox</span>
            </div>
            <h3 className="text-lg font-semibold text-foreground">No conversations yet</h3>
            <p className="mt-2 max-w-md text-sm text-muted-foreground">
              When customers send SMS messages or call your Twilio number, conversations will appear
              here.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Button
                variant="primary"
                size="md"
                className="transition-colors duration-200 hover:bg-brand-600"
              >
                Configure Twilio
              </Button>
              <NavLink
                to="/dialer"
                className="inline-flex h-10 items-center rounded-xl border border-border bg-background px-4 text-sm font-medium text-foreground transition-colors duration-200 hover:bg-muted"
              >
                Open Comms
              </NavLink>
            </div>
          </div>
        </div>

        <div className="shell-surface p-5">
          <h3 className="text-base font-semibold text-foreground">Quick setup sequence</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Prioritize these tasks to get your team from zero state to live support.
          </p>
          <div className="mt-4 space-y-3">
            {QUICK_START_STEPS.map((step, index) => (
              <div
                key={step.label}
                className="rounded-xl border border-border bg-background/80 p-3"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Step {index + 1}
                </p>
                <p className="mt-1 text-sm font-semibold text-foreground">{step.label}</p>
                <p className="mt-1 text-xs text-muted-foreground">{step.description}</p>
                <NavLink
                  to={step.to}
                  className="mt-2 inline-flex h-8 items-center rounded-lg px-2.5 text-xs font-semibold text-brand-700 transition-colors duration-200 hover:bg-brand-500/10 dark:text-brand-200 dark:hover:bg-brand-500/15"
                >
                  {step.cta}
                </NavLink>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {TEAM_METRICS.map((stat) => (
          <article
            key={stat.label}
            className="shell-surface p-5 transition-colors hover:border-brand-500/40"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-muted-foreground">{stat.label}</span>
              <span className="material-symbols-outlined text-muted-foreground">{stat.icon}</span>
            </div>
            <p className="mt-2 text-2xl font-semibold text-foreground">{stat.value}</p>
            <p className="mt-1 text-xs text-muted-foreground">{stat.helper}</p>
          </article>
        ))}
      </section>
    </div>
  );
}
